/**
 * W101 (V6.0, #152) — Radar-Buckets: Watchlist danach gruppieren, wo
 * überhaupt etwas passiert.
 *
 * Zwei Buckets:
 *   1 BEOBACHTEN — hier bewegt sich etwas: Krypto-Kauffenster, Wende-Zeichen,
 *                  Boden im Aufbau, konstruktives Chart-Muster, hoher Score
 *   2 GEDULD     — auf dem Schirm, aber kein Anlass hinzusehen
 *
 * ES GAB EINEN DRITTEN, „JETZT — heute handelbar". Er ist am 10.08.2026
 * entfernt worden, und zwar nicht wegen eines Fehlers im Code, sondern weil
 * die Überschrift etwas behauptete, das dieses Projekt gemessen widerlegt hat:
 *
 *   docs/konzepte/2026-08-08-vorregistrierte-signalpruefung.md, Zeile 37:
 *   „Alle echten Einstiege zusammen gegen die Nulllinie: −0,010 R
 *   [−0,206, +0,179]. Elf Jahre Filterarbeit im Sperrzeitraum liefern exakt
 *   das Ergebnis von blindem Kaufen."
 *
 *   Ebenda, „Ergebnis 3 — Rangfolge: keine": Fünf von neun Merkmalen drehen
 *   zwischen Entwicklung und Sperrzeit das Vorzeichen.
 *
 * Ein Etikett „Jetzt handelbar" verspricht, dass diese Gruppe besser ist als
 * der Rest. Dieser Radar sortierte dafür nach Score-Schwellen — also nach
 * genau der Rangfolge, die im Sperrzeitraum nicht standhält. Dazu kam ein
 * Zirkelschluss: `hasActivePlan` machte ein Symbol handelbar, WEIL man
 * bereits einen Plan angelegt hatte — die eigene frühere Entscheidung als
 * Beleg für sich selbst.
 *
 * ARBEITSTEILUNG SEITDEM, und sie ist der eigentliche Punkt:
 *   - Dieser Radar lenkt AUFMERKSAMKEIT. Er darf unbewiesen sein; er
 *     behauptet nur „hier bewegt sich etwas", und das ist nachprüfbar wahr.
 *   - HANDELBAR sagt ausschließlich die Zustandsmaschine über `zone_offen`
 *     (src/lib/v6/setup-state.ts) — dieselbe Quelle, aus der die
 *     Handlungsliste auf dem Dashboard speist. Nur sie nennt Limit und Stop.
 *
 * Die Sortierränge sind unveraendert geblieben. Was frueher „jetzt" war,
 * steht dadurch weiterhin oben in „beobachten" — die Reihenfolge der
 * Aufmerksamkeit war nie das Problem, nur ihr Etikett.
 *
 * Reine Funktion über die zentral verfügbaren Cron-Signale (v5_chart_events,
 * v5_crypto_boden, v5_reversal_radar) plus den Headline-Score — KEIN
 * N+1-Browser-Rechnen. Antizyklik-treu: ein Boden im Aufbau ist „beobachten",
 * kein „meiden".
 */

export type RadarBucket = 'beobachten' | 'geduld';

export interface RadarBucketInput {
  /** Headline-Score (entry_score) 0–100, falls bekannt. */
  score: number | null;
  /** Krypto-Boden: Kauffenster für Tranche 1 offen. */
  cryptoT1Ready: boolean;
  /** Krypto-Boden-Score 0–100 (auch ohne t1_ready relevant). */
  bodenScore: number | null;
  /** Reversal-Radar: erste Wende-Zeichen (Kurs über MA50, Momentum dreht). */
  stabilizing: boolean;
  /** Es existiert ein gespeicherter aktiver Plan für das Symbol. */
  hasActivePlan: boolean;
  /** Top-Chart-Ereignis-Tonalität (aus v5_chart_events). */
  chartTone: 'constructive' | 'neutral' | 'bearish' | null;
  /** Chart-Ereignis-Art (z.B. 'double_bottom', 'ma200_reclaim'). */
  chartKind: string | null;
  /** Alter des Chart-Ereignisses in Tagen (Frische-Gate; null/undefined = unbekannt). */
  chartEventAgeDays?: number | null;
}

export interface RadarBucketResult {
  bucket: RadarBucket;
  /** Rang INNERHALB des Buckets (höher = weiter oben). */
  rank: number;
  /** Ein Satz, warum das Symbol in diesem Bucket ist. */
  grund: string;
}

const BUCKET_ORDER: Record<RadarBucket, number> = { beobachten: 1, geduld: 2 };

/** Reihenfolge fürs Sortieren: Bucket, dann Rang absteigend. */
export function radarSortValue(r: RadarBucketResult): number {
  return BUCKET_ORDER[r.bucket] * 1000 - r.rank;
}

const CONSTRUCTIVE_BREAKOUT_KINDS = new Set([
  'double_bottom',
  'ma200_reclaim',
  'range_box',
  'golden_cross',
]);

/**
 * Score-Untergrenze, ab der ein Chart-Muster überhaupt hervorgehoben wird.
 * Darunter sagt die Engine-Tier-Logik selbst „abwarten" (tiers.ts: 45–64).
 *
 * Diese Schwelle entscheidet seit dem 10.08.2026 nur noch über die
 * REIHENFOLGE innerhalb von „beobachten", nicht mehr über ein Etikett
 * „handelbar". Das ist der Unterschied, auf den es ankommt: Eine unbewiesene
 * Rangfolge darf bestimmen, worauf man zuerst schaut — sie darf nicht
 * behaupten, dass sich das Hinschauen auszahlt.
 */
const PATTERN_MIN_SCORE = 55;
/** Ab wann ein Ausbruch als abgestanden gilt und nach unten rutscht. */
const PATTERN_FRESH_DAYS = 21;

export function classifyRadarBucket(input: RadarBucketInput): RadarBucketResult {
  const score = input.score ?? 0;

  // ── 1) BEOBACHTEN, oberer Bereich — hier bewegt sich am meisten ─────────
  //
  // Diese drei Zweige lieferten bis zum 10.08.2026 den Bucket „jetzt". Die
  // Raenge sind absichtlich unveraendert: Die Reihenfolge war nie das
  // Problem, nur die Ueberschrift. Die Begruendungssaetze sagen jetzt, was
  // beobachtet wurde — nicht, was zu tun ist. Was zu tun ist, sagt die
  // Zustandsmaschine.
  if (input.cryptoT1Ready) {
    return {
      bucket: 'beobachten',
      rank: 95 + Math.round((input.bodenScore ?? 0) / 20),
      grund: `Kauf-Fenster offen (Boden-Score ${Math.round(input.bodenScore ?? 0)}) — Tranche 1 wäre bereit.`,
    };
  }
  if (input.hasActivePlan) {
    return {
      bucket: 'beobachten',
      rank: 90,
      // Zirkelschluss-Warnung im Satz selbst: Ein Plan ist deine eigene
      // frueherer Entscheidung, kein Befund ueber den Markt.
      grund: 'Du hast hier einen Plan hinterlegt — er wartet auf seinen Einstiegspreis.',
    };
  }
  if (
    input.chartTone === 'constructive' &&
    input.chartKind !== null &&
    CONSTRUCTIVE_BREAKOUT_KINDS.has(input.chartKind)
  ) {
    const age = input.chartEventAgeDays;
    const fresh = age == null || age <= PATTERN_FRESH_DAYS;
    const scoreOk = score >= PATTERN_MIN_SCORE;
    if (fresh && scoreOk) {
      return {
        bucket: 'beobachten',
        rank: 80 + Math.round(score / 10),
        grund: 'Chart zeigt ein frisches Kauf-Muster — ansehen.',
      };
    }
    const warum = !scoreOk
      ? `Setup-Score noch schwach (${Math.round(score)})`
      : `Ausbruch schon ${Math.round(age ?? 0)} Tage her`;
    return {
      bucket: 'beobachten',
      rank: 45 + Math.round(score / 10),
      grund: `Chart-Kaufmuster da, aber ${warum}.`,
    };
  }

  // ── 2) BEOBACHTEN, unterer Bereich ──────────────────────────────────────
  if (input.stabilizing) {
    return {
      bucket: 'beobachten',
      rank: 70 + Math.round(score / 10),
      grund: 'Erste Wende-Zeichen (über MA50, Momentum dreht) — Tranchen-Limits prüfen.',
    };
  }
  if (input.bodenScore !== null && input.bodenScore >= 60) {
    return {
      bucket: 'beobachten',
      rank: 55 + Math.round(input.bodenScore / 10),
      grund: `Boden im Aufbau (Score ${Math.round(input.bodenScore)}) — auf das Kauf-Fenster warten.`,
    };
  }
  if (score >= 65) {
    return {
      bucket: 'beobachten',
      rank: 40 + Math.round(score / 10),
      grund: `Hoher Score (${Math.round(score)}), aber noch kein Einstiegssignal.`,
    };
  }
  if (input.chartTone === 'constructive') {
    return {
      bucket: 'beobachten',
      rank: 35,
      grund: 'Konstruktives Chart-Bild — entwickelt sich, noch kein Trigger.',
    };
  }

  // ── 3) GEDULD — auf dem Schirm, kein Signal ────────────────────────────
  return {
    bucket: 'geduld',
    rank: Math.round(score),
    grund:
      input.chartTone === 'bearish'
        ? 'Chart noch im Abwärtsdruck — abwarten.'
        : 'Auf dem Schirm, aktuell kein Signal.',
  };
}

export const RADAR_BUCKET_META: Record<
  RadarBucket,
  // 'green' ist bewusst nicht mehr vergeben: Grün stand für „handelbar",
  // und das sagt der Radar nicht mehr. Der Wert bleibt im Typ, weil
  // StatusDot ihn kennt — hier vergibt ihn nur niemand.
  { label: string; hint: string; tone: 'green' | 'yellow' | 'gray' }
> = {
  beobachten: {
    label: 'Beobachten',
    hint:
      'Hier bewegt sich etwas: Kauf-Fenster, Wende-Zeichen, Boden im Aufbau oder ein '
      + 'Chart-Muster. Das ist Aufmerksamkeit, kein Kaufsignal — ob ein Wert handelbar '
      + 'ist, sagt allein die Kaufzone der Zustandsmaschine.',
    tone: 'yellow',
  },
  geduld: {
    label: 'Geduld',
    hint: 'Auf dem Schirm, aber aktuell kein Signal — nichts zu tun.',
    tone: 'gray',
  },
};
